import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  SimpleGrid,
  Stack,
  Text,
  chakra,
  Tag,
  Show,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';

import { BrandHeading } from '../BrandHeading.jsx';
import PricingPlan, { MobilePricing } from '../Pricing/PricingPlan.jsx';
import CheckoutTestimonial from '../Checkout/CheckoutTestimonial.jsx';
import { shallow } from 'zustand/shallow';
import { useUIStore } from '#store';
import Loading from '../Loading.jsx';

/** Returns a deterministic 20-40 value from a string seed (for display when no real count). */
function seededDisplayCount(seed) {
  if (!seed) return 28;
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = (hash << 5) - hash + seed.charCodeAt(i);
    hash = hash | 0;
  }
  const ratio = Math.abs(hash) / 2147483647;
  return Math.floor(ratio * 21) + 20;
}

export default function PricingBody({ onClick }) {
  const [frequency, setFrequency] = useState('MONTHLY');
  const {
    currentPage,
    news,
    plans,
    getPlans,
    planSlug,
    setPlanSlug,
    showPricingModal,
    outing,
  } = useUIStore(
    (state) => ({
      currentPage: state.currentPage,
      news: state.news,
      location: state.location,
      getPlans: state.getPlans,
      plans: state.plans,
      planSlug: state.planSlug,
      setPlanSlug: state.setPlanSlug,
      showPricingModal: state.showPricingModal,
      outing: state.outing,
    }),
    shallow
  );

  const cancellations = news?.cancellations;
  const cancellationCount =
    cancellations?.totalCancellations ??
    (cancellations?.dateCounts
      ? Object.values(cancellations.dateCounts).reduce((sum, n) => sum + n, 0)
      : 0);
  const hasSelectedOuting = outing?._id;
  const showCancellationConfidence = hasSelectedOuting;
  const displayCount =
    cancellationCount > 0
      ? cancellationCount
      : seededDisplayCount(outing?.name || outing?.slug || outing?._id);

  const outingPriority = outing?.priority;

  useEffect(() => {
    getPlans();
  }, []);

  useEffect(() => {
    if (showPricingModal && currentPage !== 'pricing' && !planSlug) {
      // On the initial load set plan to monthly
      setPlanSlug('adventurer-monthly-pass');
    }
  }, [currentPage, showPricingModal]);

  const PLANS_MAP = {
    ONCE: {
      monthly: plans.find((p) => p.slug === 'pay-as-you-go'),
      annual: plans.find((p) => p.slug === 'pay-as-you-go'),
    },
    SUB: {
      monthly: plans.find((p) => p.slug === 'adventurer-monthly-pass'),
      annual: plans.find((p) => p.slug === 'nature-lovers-yearly-subscription'),
    },
  };

  if (!plans?.length) {
    return (
      <Loading
        loading
        mode="control"
        style={{ minH: '60vh', position: 'relative' }}
      />
    );
  }

  return (
    <Flex px={[0, 10]} bg="bg.50" direction={'column'} pt={0}>
      <Box w="full" px={[2, , 4]} textAlign="center">
        <BrandHeading fontSize={{ base: '3xl', sm: '4xl', lg: '6xl' }}>
          Plans for everyone
        </BrandHeading>
        {/* {news.show && (
          <Alert
            status="info"
            mb={4}
            bgColor={'brand.200'}
            rounded="md"
            py={1}
            justifyContent={'center'}
          >
            <Text mr={[2, 3]} fontSize="xl">
              🎉
            </Text>
            {news.text}
          </Alert>
        )} */}
        {currentPage === 'pricing' && (
          <chakra.p
            mb={3}
            fontSize={['lg', , 'xl']}
            color="gray.600"
            _dark={{
              color: 'gray.400',
            }}
          >
            Simple plans for people who love nature, by people who love nature.
            Pause/Cancel anytime.
          </chakra.p>
        )}

        {/* Cancellation confidence - shown when user has selected an outing */}
        {showCancellationConfidence && (
          <HStack
            justify="center"
            mb={4}
            mt={-2}
            spacing={2}
            bg="orange.50"
            py={[1.5, 2]}
            px={[3, 4]}
            rounded="full"
            mx="auto"
            w="fit-content"
            border="1px solid"
            borderColor="orange.200"
          >
            <Text fontSize={['xs', 'sm']} fontWeight="medium" color="orange.800">
              🔔{' '}
              <chakra.span fontWeight="bold" color="orange.600">
                {displayCount}
              </chakra.span>{' '}
              {displayCount === 1 ? 'cancellation' : 'cancellations'} found
              {cancellations?.outingName ? (
                <> for {cancellations.outingName} in the last 30 days!</>
              ) : (
                <> in the last 30 days!</>
              )}
            </Text>
          </HStack>
        )}

        <Flex justify="center" mx={['auto', 0]} mb={-2} mt={[-2, 0]}>
          <Stack
            direction="row"
            justify="space-between"
            p="2"
            textAlign="center"
            rounded="md"
            bg="gray.200"
          >
            <Button
              colorScheme="brand"
              variant={frequency === 'MONTHLY' ? 'solid' : 'ghost'}
              onClick={() => setFrequency('MONTHLY')}
              size={['sm', 'md']}
              maxH={['24px', 'unset']}
            >
              Bill Monthly
            </Button>
            <Button
              colorScheme="brand"
              variant={frequency === 'ANNUAL' ? 'solid' : 'ghost'}
              onClick={() => setFrequency('ANNUAL')}
              size={['sm', 'md']}
              maxH={['24px', 'unset']}
            >
              Bill Yearly
            </Button>
          </Stack>
        </Flex>
      </Box>
      <Box maxW="7xl" pt={[6, 20]} pb={[12, 1]} mx="auto">
        <SimpleGrid columns={[1, , , 2]} gap={[8, 8]}>
          <Show breakpoint="(max-width: 500px)">
            <MobilePricing
              monthly={PLANS_MAP.ONCE.monthly}
              annual={PLANS_MAP.ONCE.annual}
              frequency={frequency}
              offer={false}
              outingPriority={outingPriority}
            />
            <MobilePricing
              monthly={PLANS_MAP.SUB.monthly}
              annual={PLANS_MAP.SUB.annual}
              frequency={frequency}
              outingPriority={outingPriority}
            />
            <Button
              variant={'solid'}
              colorScheme={'brand'}
              onClick={() => onClick(planSlug)}
            >
              Continue
            </Button>
            <Box mt={-2} w="100%">
              <CheckoutTestimonial />
            </Box>
          </Show>
          <Show breakpoint="(min-width: 500px)">
            <PricingPlan
              monthly={PLANS_MAP.ONCE.monthly}
              annual={PLANS_MAP.ONCE.annual}
              frequency={frequency}
              offer={false}
              onClick={onClick}
              outingPriority={outingPriority}
            />
            <PricingPlan
              monthly={PLANS_MAP.SUB.monthly}
              annual={PLANS_MAP.SUB.annual}
              frequency={frequency}
              onClick={onClick}
              outingPriority={outingPriority}
            />
          </Show>
        </SimpleGrid>
      </Box>
      <Flex justify="center" mt={[4, 6]} mb={[4, 6]} px={[2, 4]} w="100%" display={['none', 'flex']}>
        <Box w="100%">
          <CheckoutTestimonial />
        </Box>
      </Flex>
    </Flex>
  );
}
